import { ConsentTrackingStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  DialogHeading,
  DialogCloseX,
  DialogButton,
  DialogBody,
  useToasterContext,
  Dialog,
  DialogContent,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import styles from "./Sharing.module.scss"

import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useUpdateConsents } from "src/hooks/consent"
import { SavingResponseErrorToast } from "src/toasts/SavingResponseErrorToast"

interface Props {
  open: boolean;
  setOpen: (o: boolean) => void;
}

const polyglotPrefix =
  "pages.data_sharing.invite_a_follower.share_data_consent_dialog."

export function DataShareConsentDialog(props: Props) {
  const {
    open: isOpen, setOpen,
  } = props

  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const setConsent = useUpdateConsents()

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const acknowledgeConsent = useCallback(
    async (accepted: boolean) => {
      setIsOverlayLoaderVisible(true)

      try {
        if (!setConsent) {
          throw new Error("cannot update consents")
        }

        await setConsent({
          share_consent: {
            status: accepted ? ConsentTrackingStatus.Accepted : ConsentTrackingStatus.Declined,
          },
        })

        if (accepted) {
          void navigate({ to: "/data_sharing/sharing/invite" })
        } else {
          setOpen(false)
        }
      } catch {
        showToast(
          "saving-response-error-toast",
          SavingResponseErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      setConsent,
      showToast,
      setOpen,
      navigate,
      setIsOverlayLoaderVisible,
    ],
  )

  const onAccept = useCallback(
    () => {
      void acknowledgeConsent(true)
    },
    [acknowledgeConsent],
  )

  const onReject = useCallback(
    () => {
      void acknowledgeConsent(false)
    },
    [acknowledgeConsent],
  )

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setOpen}
    >
      <DialogContent>
        <DialogHeading>
          {polyglot.t(`${polyglotPrefix}heading`)}
          <DialogCloseX />
        </DialogHeading>
        <DialogBody>
          <div id="consent-dialog-description-one">
            {polyglot.t(`${polyglotPrefix}description_one`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}description_two`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}description_three`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}description_four`)}
          </div>
          <a
            className={styles.link}
            href="https://www.tandemdiabetes.com/legal/privacy/hipaa-notice-of-privacy-practices"
            target="_blank"
            rel="noreferrer"
            id="privacy-notice"
          >
            {polyglot.t(`${polyglotPrefix}privacy_notice_link`)}
          </a>
        </DialogBody>
        <DialogButton
          stylePreset="primary"
          id="accept-button"
          onClick={onAccept}
        >
          {polyglot.t(`${polyglotPrefix}ok_cta`)}
        </DialogButton>
        <DialogButton
          stylePreset="secondary"
          id="cancel-button"
          onClick={onReject}
        >
          {polyglot.t(`${polyglotPrefix}cancel_cta`)}
        </DialogButton>
      </DialogContent>
    </Dialog>
  )
}
